"use client";

import { NumericFormat } from "react-number-format";
import { useParcelas } from "./_hook";
import { usePrenotaStore } from "@PN/inclusao";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  fmt,
  parse,
  Label,
  Button,
  Input,
  DatePicker,
  cn,
  ScrollArea,
} from "visuais";
import { SimpleAuthForm } from "./SimpleAuthForm";
import { useMemo, useState, useEffect } from "react";
import { IconRefresh } from "@tabler/icons-react";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  validateAllParcelas,
  validateParcelaDate,
} from "./rules/parcelasRules";

export function ParcelasTable() {
  const {
    parcelas,
    isLoading,
    isFetching,
    error,
    areParcelasSaved,
    handleVencimentoChange,
    handleValorChange,
    handleAddParcela,
    handleRemoveParcela,
    handleForceRefetch,
    valorNF,
    refetch,
    parcelasBaseProtheus,
    isParcelaBaseProtheus,
  } = useParcelas();

  const pagamentos = usePrenotaStore((state) => state.draft.pagamentos);
  const documento = usePrenotaStore((state) => state.draft.documento);

  const especieDocumento = (documento?.especie ?? "").trim().toUpperCase();
  const condicaoFinanceiraCodigo = (pagamentos.condicaoFinanceira ?? "").trim().toUpperCase();
  const isCondicaoSemValidacaoVencimento = condicaoFinanceiraCodigo === "C35";

  const aplicaRegraDmais3 =
    especieDocumento !== "DOC" &&
    especieDocumento !== "FAT" &&
    especieDocumento !== "CTE" &&
    condicaoFinanceiraCodigo !== "C01" &&
    condicaoFinanceiraCodigo !== "C27" &&
    !isCondicaoSemValidacaoVencimento;

  const parseVencimentoToMs = (raw?: string) => {
    if (!raw || typeof raw !== "string") return null;
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const d = new Date(
        parseInt(isoMatch[1], 10),
        parseInt(isoMatch[2], 10) - 1,
        parseInt(isoMatch[3], 10)
      );
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    const brMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (brMatch) {
      const d = new Date(
        parseInt(brMatch[3], 10),
        parseInt(brMatch[2], 10) - 1,
        parseInt(brMatch[1], 10)
      );
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    return null;
  };
  const getValidationVencimentoMs = (raw?: string) => {
    const baseMs = parseVencimentoToMs(raw);
    if (baseMs === null) return null;
    const adjusted = new Date(baseMs);
    const dayOfWeek = adjusted.getDay();
    if (dayOfWeek === 6) adjusted.setDate(adjusted.getDate() + 2);
    if (dayOfWeek === 0) adjusted.setDate(adjusted.getDate() + 1);
    adjusted.setHours(0, 0, 0, 0);
    return adjusted.getTime();
  };

  const dMais3InvalidIdxSet = useMemo(() => {
    if (!aplicaRegraDmais3 || !parcelas.length) return new Set<number>();
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const limiteBloqueio = new Date(hoje);
    limiteBloqueio.setDate(limiteBloqueio.getDate() + 3);
    const hojeMs = hoje.getTime();
    const limiteMs = limiteBloqueio.getTime();
    const invalidos = new Set<number>();
    parcelas.forEach((parcela, idx) => {
      const vencimentoMs = getValidationVencimentoMs(parcela.vencimento);
      if (vencimentoMs === null) return;
      if (vencimentoMs >= hojeMs && vencimentoMs <= limiteMs) {
        invalidos.add(idx);
      }
    });
    return invalidos;
  }, [aplicaRegraDmais3, parcelas]);
  const vencidasInvalidIdxSet = useMemo(() => {
    if (isCondicaoSemValidacaoVencimento) return new Set<number>();
    if (!parcelas.length) return new Set<number>();
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const hojeMs = hoje.getTime();
    const invalidos = new Set<number>();
    parcelas.forEach((parcela, idx) => {
      const vencimentoMs = getValidationVencimentoMs(parcela.vencimento);
      if (vencimentoMs === null) return;
      if (vencimentoMs < hojeMs) {
        invalidos.add(idx);
      }
    });
    return invalidos;
  }, [isCondicaoSemValidacaoVencimento, parcelas]);

  // Cálculo do total das parcelas
  const totalParcelas = parcelas.reduce((sum, p) => sum + (p.valor || 0), 0);
  const isValorTotalValido =
    valorNF === 0 || Math.abs(totalParcelas - valorNF) < 0.01;

  // Estado para mostrar feedback de salvamento automático
  const [showAutoSaveFeedback, setShowAutoSaveFeedback] = useState(false);

  // Estado para controlar se deve mostrar o formulário de login
  const [showAuthForm, setShowAuthForm] = useState(false);

  // Mostra feedback quando parcelas são salvas automaticamente
  useEffect(() => {
    if (parcelas.length > 0 && areParcelasSaved) {
      setShowAutoSaveFeedback(true);
      const timer = setTimeout(() => setShowAutoSaveFeedback(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [parcelas.length, areParcelasSaved]);

  // Detecta erro 401 e mostra formulário de login
  useEffect(() => {
    if (error && "response" in error && error.response?.status === 401) {
      setShowAuthForm(true);
    } else {
      setShowAuthForm(false);
    }
  }, [error]);

  // Memoiza os objetos Date para cada parcela
  const vencDates = useMemo(
    () =>
      parcelas.map((parcela) => {
        if (
          parcela.vencimento &&
          /^\d{4}-\d{2}-\d{2}$/.test(parcela.vencimento)
        ) {
          // ✅ CORRIGIDO: Usa parse consistente para evitar problemas de timezone
          return parse.dates.ISO(parcela.vencimento, { allowNull: true });
        }
        return parse.dates.ISO(parcela.vencimento, { allowNull: true });
      }),
    [parcelas]
  );
  const canEditVencimento = true;
  const canEditValor = false;
  const canManageParcelas = false;
  const [invalidDateRows, setInvalidDateRows] = useState<Record<string, boolean>>(
    {}
  );
  const [invalidValueRows, setInvalidValueRows] = useState<Record<string, boolean>>(
    {}
  );
  const validationSummary = useMemo(
    () =>
      validateAllParcelas({
        parcelas,
        parcelasBaseProtheus: parcelasBaseProtheus || {},
        isRegraAtiva: true,
      }),
    [
      parcelas,
      parcelasBaseProtheus,
    ]
  );

  const isAllowedVencimentoDate = (idx: number, selectedDate: Date) =>
    validateParcelaDate({
      parcelas,
      idx,
      selectedDate,
      parcelasBaseProtheus: parcelasBaseProtheus || {},
      isRegraAtiva: true,
    });

  useEffect(() => {
    setInvalidDateRows((prev) => {
      const next: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (parcelas.some((p) => p.item === key) && value) next[key] = true;
      }
      return next;
    });
    setInvalidValueRows((prev) => {
      const next: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (parcelas.some((p) => p.item === key) && value) next[key] = true;
      }
      return next;
    });
  }, [parcelas.length]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground">
            Parcelas
          </Label>
          {isFetching && parcelas.length > 0 && (
            <span className="text-[10px] text-cyan-600 dark:text-cyan-400 animate-pulse">
              Atualizando...
            </span>
          )}
        </div>
        {/* Mostrar total das parcelas e botão de refresh */}
        {parcelas.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="text-xs text-muted-foreground">
              Total: {fmt.numbers.currency(totalParcelas)}
            </div>
            <Button
              onClick={handleForceRefetch}
              disabled={isFetching}
              size="sm"
              variant="secondary"
              className="group size-5 hover:size-6 bg-cyan-200/70 hover:bg-cyan-200/80 dark:bg-cyan-700/70 dark:hover:bg-cyan-700/80 transition-all duration-300 disabled:opacity-50"
              title="Atualizar parcelas da API"
            >
              <IconRefresh className="size-4 group-hover:size-5 transition-all duration-300" />
            </Button>
          </div>
        )}
      </div>

      {/* Mostrar erro se houver */}
      {error && !showAuthForm && (
        <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
          <div className="font-medium mb-1">Erro ao carregar parcelas:</div>
          <div className="text-muted-foreground">
            {error instanceof Error ? error.message : String(error)}
          </div>
          <Button onClick={() => refetch()} size="sm" className="mt-2">
            Tentar novamente
          </Button>
        </div>
      )}

      {/* Mostrar formulário de login para erro 401 */}
      {showAuthForm && (
        <SimpleAuthForm
          onSuccess={() => {
            setShowAuthForm(false);
            refetch(); // Tenta buscar as parcelas novamente após login
          }}
          onCancel={() => {
            setShowAuthForm(false);
          }}
        />
      )}

      <div className="space-y-3">
        {/* Tabela com shadcn */}
        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-[280px]">
            <ScrollArea className="h-[280px]">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow className="bg-muted/50">
                  <TableHead className="text-center w-20 text-xs font-medium">
                    Parc.
                  </TableHead>
                  <TableHead className="w-40 text-xs font-medium">
                    Vencimento
                  </TableHead>
                  <TableHead className="text-right text-xs font-medium">
                    Valor
                  </TableHead>
                  {canManageParcelas && (
                    <TableHead className="w-10 text-xs font-medium" />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcelas.length > 0 ? (
                  parcelas.map((parcela, idx) => {
                    const vencDate = vencDates[idx];
                    const isParcelaBase = isParcelaBaseProtheus(idx);
                    const canRemoveCurrentParcela =
                      canManageParcelas && !isParcelaBase && parcelas.length > 1;
                    const hasVencimentoError = !parcela.vencimento;
                    const itemErrors = validationSummary.errorsByItem[parcela.item] || [];
                    const hasInvalidDateError =
                      !!invalidDateRows[parcela.item] ||
                      itemErrors.some((error) => /data/i.test(error));
                    const hasVencidaError = vencidasInvalidIdxSet.has(idx);
                    const hasDmais3Error = dMais3InvalidIdxSet.has(idx);
                    const hasInvalidValueError =
                      !!invalidValueRows[parcela.item] ||
                      itemErrors.some((error) => /valor/i.test(error));
                    return (
                      <TableRow
                        key={parcela.item || idx}
                        className={cn(
                          "hover:bg-muted/30",
                          (hasDmais3Error || hasVencidaError) && "bg-destructive/5"
                        )}
                      >
                        <TableCell className="text-center w-20 py-2">
                          <Input
                            value={parcela.item}
                            disabled
                            className="h-8 text-xs text-center border-0 bg-transparent font-medium"
                          />
                        </TableCell>
                        <TableCell className="w-40 py-2">
                          <DatePicker
                            value={vencDate || undefined}
                            placeholder="dd/mm/aaaa"
                            disabled={!canEditVencimento || isFetching}
                            className={cn(
                              "h-8 text-xs",
                                hasVencimentoError ||
                                  hasInvalidDateError ||
                                  hasVencidaError ||
                                  hasDmais3Error
                                  ? "border-destructive focus:border-destructive"
                                  : ""
                            )}
                            onChange={(date) => {
                              if (date && "toDateString" in date) {
                                const nextDate = date as Date;
                                const result = isAllowedVencimentoDate(idx, nextDate);
                                if (!result.allowed) {
                                  setInvalidDateRows((prev) => ({
                                    ...prev,
                                    [parcela.item]: true,
                                  }));
                                  toast.error(
                                    result.message ||
                                      `Parcela ${idx + 1}: data não permitida. O vencimento não foi alterado.`,
                                    { duration: 5000 }
                                  );
                                  return;
                                }
                                setInvalidDateRows((prev) => {
                                  if (!prev[parcela.item]) return prev;
                                  const next = { ...prev };
                                  delete next[parcela.item];
                                  return next;
                                });
                                handleVencimentoChange(idx, date as Date);
                              }
                            }}
                            isDateDisabled={(date) => {
                              if (isCondicaoSemValidacaoVencimento) {
                                return !isAllowedVencimentoDate(idx, date).allowed;
                              }
                              const hoje = new Date();
                              hoje.setHours(0, 0, 0, 0);
                              const corrente = new Date(date);
                              corrente.setHours(0, 0, 0, 0);
                              if (corrente.getTime() < hoje.getTime()) return true;
                              return !isAllowedVencimentoDate(idx, date).allowed;
                            }}
                            allowTyping={true}
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <NumericFormat
                            value={parcela.valor}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="R$ "
                            customInput={Input}
                            disabled={!canEditValor}
                            onValueChange={({ floatValue }) => {
                              const nextValor = floatValue ?? 0;
                              setInvalidValueRows((prev) => {
                                if (!prev[parcela.item]) return prev;
                                const next = { ...prev };
                                delete next[parcela.item];
                                return next;
                              });
                              handleValorChange(idx, nextValor);
                              // Feedback visual temporário
                              const element = document.querySelector(
                                `[data-parcela-valor="${idx}"]`
                              );
                              if (element) {
                                element.classList.add(
                                  "bg-cyan-50",
                                  "dark:bg-cyan-900/30",
                                  "border-cyan-300",
                                  "dark:border-cyan-600"
                                );
                                setTimeout(() => {
                                  element.classList.remove(
                                    "bg-cyan-50",
                                    "dark:bg-cyan-900/30",
                                    "border-cyan-300",
                                    "dark:border-cyan-600"
                                  );
                                }, 500);
                              }
                            }}
                            allowNegative={false}
                            className={cn(
                              "h-8 text-xs text-right transition-all duration-200 hover:border-primary/50 focus:border-primary",
                              hasInvalidValueError
                                ? "border-destructive focus:border-destructive"
                                : ""
                            )}
                            data-parcela-valor={idx}
                          />
                        </TableCell>
                        {canManageParcelas && (
                          <TableCell className="py-2 w-10">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveParcela(idx)}
                              disabled={!canRemoveCurrentParcela}
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 disabled:opacity-30"
                              title={
                                isParcelaBase
                                  ? "Parcela base do Protheus não pode ser removida"
                                  : "Remover parcela"
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={canManageParcelas ? 4 : 3}
                      className="text-center py-8 text-muted-foreground text-sm"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                          <span>Carregando parcelas...</span>
                        </div>
                      ) : (
                        "Nenhuma parcela encontrada"
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table></ScrollArea>
          </div>
        </div>

        {canManageParcelas && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddParcela}
            className="w-full text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Adicionar parcela
          </Button>
        )}

        {/* Validação do total */}
        {parcelas.length > 0 && !isValorTotalValido && valorNF !== null && (
          <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
            <div className="font-medium mb-1">Divergência de valores:</div>
            <div className="space-y-1">
              <div>
                Total das parcelas: {fmt.numbers.currency(totalParcelas)}
              </div>
              <div>Valor da NF: {fmt.numbers.currency(valorNF)}</div>
            </div>
          </div>
        )}

        {/* Bloqueio de vencimento passado */}
        {vencidasInvalidIdxSet.size > 0 && (
          <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
            <div className="font-medium mb-1">
              Há {vencidasInvalidIdxSet.size} parcela(s) com vencimento anterior a hoje.
            </div>
            <div>O lançamento é bloqueado quando houver vencimento já vencido.</div>
          </div>
        )}

        {/* Bloqueio D+3 (não se aplica para DOC/FAT/CTE ou condição C01/C27/C35) */}
        {aplicaRegraDmais3 && dMais3InvalidIdxSet.size > 0 && (
          <div className="text-xs text-destructive p-3 bg-destructive/10 dark:bg-destructive/20 rounded-md border border-destructive/20 dark:border-destructive/30">
            <div className="font-medium mb-1">
              Há {dMais3InvalidIdxSet.size} parcela(s) com vencimento entre hoje e D+3.
            </div>
            <div>
              O lançamento é bloqueado quando qualquer vencimento estiver dentro
              de 3 dias corridos a partir de hoje.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
